import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { ApiKeyFilterTabs, ApiKeyType } from './components/ApiKeyFilterTabs';
import { ApiKeyTable, Row } from './components/ApiKeyTable';
import { CreateApiKeyModal } from './components/CreateApiKeyModal';
import { entities, mockData } from './mock';
import { keyTypes, roles } from './type';

const thisOrg = 'Amce Organization';

const ApiKeyManagementPage = () => {
  const [selectedType, setSelectedType] = useState<ApiKeyType>('all');
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>(() => mockData);
  const pageSize = 8;

  const comparators: Record<
    | 'name'
    | 'type'
    | 'role'
    | 'entity'
    | 'createdBy'
    | 'createdAt'
    | 'lastUsed'
    | 'status',
    (a: Row, b: Row) => number
  > = {
    name: (a, b) => a.name.localeCompare(b.name),
    type: (a, b) => a.type.localeCompare(b.type),
    role: (a, b) => a.role.localeCompare(b.role),
    entity: (a, b) => a.entity.localeCompare(b.entity),
    createdBy: (a, b) => a.createdBy.localeCompare(b.createdBy),
    createdAt: (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    lastUsed: (a, b) =>
      new Date(a.lastUsed).getTime() - new Date(b.lastUsed).getTime(),
    status: (a, b) => a.status.localeCompare(b.status),
  };

  const filteredRows = rows.filter(
    (row) =>
      (selectedType === 'all' || row.type === selectedType) &&
      (!selectedEntity || row.entity === selectedEntity),
  );

  const typeOptions = React.useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(rows.map((row) => row.type)),
    ) as ApiKeyType[];
    return uniqueTypes.sort();
  }, [rows]);

  const entityOptions = React.useMemo(() => {
    const uniqueEntities = Array.from(new Set(rows.map((row) => row.entity)));
    return uniqueEntities.sort();
  }, [rows]);

  const paginatedRows = React.useMemo(
    () =>
      filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredRows, currentPage],
  );

  const handleSort = (sortBy: keyof Row, sortOrder: 'asc' | 'desc') => {
    setRows((current) => {
      return [...current].sort((a, b) => {
        const [item, compareTo] = sortOrder === 'asc' ? [a, b] : [b, a];
        return comparators[sortBy](item, compareTo);
      });
    });
  };

  const handleRename = () => {
    // TODO: Implement rename functionality
  };

  const handleRevoke = () => {
    // TODO: Implement revoke functionality
  };

  return (
    <div className="flex flex-col p-4">
      <Card className="shrink-0">
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Manage API keys for accessing entities in your organization
          </CardDescription>
          <div className="flex items-center justify-between">
            <ApiKeyFilterTabs
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              typeOptions={typeOptions}
              selectedEntity={selectedEntity}
              onEntityChange={setSelectedEntity}
              entityOptions={entityOptions}
            />
            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
              Create API Key
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <ApiKeyTable
            rows={paginatedRows}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={filteredRows.length}
            onPageChange={setCurrentPage}
            onSort={handleSort}
            onRename={handleRename}
            onRevoke={handleRevoke}
          />
        </CardBody>
      </Card>

      <CreateApiKeyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onKeyCreated={(newKey) => {
          setRows((current) => [newKey, ...current]);
          setIsModalOpen(false);
        }}
        keyTypes={keyTypes}
        roles={roles}
        entities={entities}
        thisOrg={thisOrg}
      />
    </div>
  );
};

export { ApiKeyManagementPage };
